import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  update: () => void;
  draw: () => void;
}

const NotFoundPage = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // 保存确定的非空引用
    const savedCanvas = canvas;
    const savedCtx = ctx;
    
    savedCanvas.width = window.innerWidth;
    savedCanvas.height = window.innerHeight;

    const particles: Particle[] = [];
    
    class ParticleClass implements Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      
      constructor() {
        this.x = Math.random() * savedCanvas.width;
        this.y = Math.random() * savedCanvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > savedCanvas.width) this.x = Math.random() * savedCanvas.width;
        if (this.y < 0 || this.y > savedCanvas.height) this.y = Math.random() * savedCanvas.height;
      }
      
      draw() {
        savedCtx.fillStyle = '#2563eb';
        savedCtx.globalAlpha = 0.4;
        savedCtx.beginPath();
        savedCtx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        savedCtx.fill();
      }
    }

    function createParticles() {
      for (let i = 0; i < 60; i++) particles.push(new ParticleClass());
    }
    
    function animate() {
      savedCtx.clearRect(0, 0, savedCanvas.width, savedCanvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      requestAnimationFrame(animate);
    }
    
    createParticles();
    animate();

    const handleResize = () => {
      savedCanvas.width = window.innerWidth;
      savedCanvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="not-found-page">
      <canvas ref={canvasRef} id="particles"></canvas>
      
      <div className="not-found-content">
        <div className="error-code">404</div>
        <h1 className="error-title">页面未找到</h1>
        <div className="divider"></div>
        <p className="error-message">
          {/* 您要查找的页面可能已被移除、名称已更改或暂时不可用。 */}
          网站内容分批上线，感谢您的访问。
        </p>
        <Link to="/" className="home-link">
          <svg className="home-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          返回首页
        </Link>
      </div>
      
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        :root {
          --primary: #2563eb;
          --secondary: #7c3aed;
          --dark: #0f172a;
          --light-bg: #f0f7ff;
          --card-bg: #ffffff;
          --text: #1e293b;
          --text-light: #64748b;
          --gradient: linear-gradient(135deg, #2563eb, #7c3aed);
          --glow: 0 0 12px rgba(37, 99, 235, 0.3);
        }

        .not-found-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--light-bg);
          color: var(--text);
          overflow: hidden;
          position: relative;
          padding: 20px;
        }

        #particles {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          opacity: 0.1;
        }

        .not-found-content {
          text-align: center;
          background: var(--card-bg);
          padding: 60px 40px;
          border-radius: 16px;
          border: 2px solid rgba(37, 99, 235, 0.15);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
          max-width: 500px;
          width: 100%;
          animation: fadeIn 0.6s ease-out;
          position: relative;
          z-index: 1;
        }

        .not-found-content:hover {
          border-color: var(--primary);
          box-shadow: var(--glow);
          transition: 0.4s;
        }

        .error-code {
          font-size: 120px;
          font-weight: 800;
          background: var(--gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          color: transparent;
          line-height: 1;
          margin-bottom: 10px;
          letter-spacing: -5px;
        }

        .error-title {
          font-size: 32px;
          color: var(--text);
          margin-bottom: 20px;
          font-weight: 600;
        }

        .divider {
          width: 80px;
          height: 3px;
          background: var(--gradient);
          margin: 0 auto 30px;
          border-radius: 2px;
        }

        .error-message {
          color: var(--text-light);
          font-size: 16px;
          line-height: 1.8;
          margin-bottom: 40px;
          font-weight: 400;
        }

        .home-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--gradient);
          color: white;
          padding: 14px 32px;
          border-radius: 50px;
          font-size: 16px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .home-link:hover {
          transform: translateY(-2px);
          box-shadow: var(--glow);
          color: white;
          text-decoration: none;
        }

        .home-icon {
          width: 18px;
          height: 18px;
          transition: transform 0.3s ease;
        }

        .home-link:hover .home-icon {
          transform: scale(1.1);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .error-code {
            font-size: 80px;
          }
          
          .error-title {
            font-size: 24px;
          }
          
          .not-found-content {
            padding: 40px 25px;
            border-radius: 12px;
          }
          
          .home-link {
            padding: 12px 28px;
            font-size: 14px;
          }
          
          .error-message {
            font-size: 14px;
          }
        }

        @media (max-width: 480px) {
          .error-code {
            font-size: 60px;
            letter-spacing: -3px;
          }
          
          .error-title {
            font-size: 20px;
          }
          
          .not-found-content {
            padding: 30px 20px;
          }
          
          .home-link {
            padding: 10px 24px;
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default NotFoundPage;